import { useMemo } from 'react';
import { useGameContext } from '../context/GameContext';
import { useLeagueSettings } from '../context/LeagueSettingsContext';
import {
  canViewerSeeLockers,
  canViewerSeeLocks,
  countUserLocksForWeek,
  shouldShowGameLocksSection,
  shouldShowLockPickInfo,
  shouldShowLockUsername,
} from '../utils/lockVisibilityUtils';

/**
 * Per-week lock visibility for the current viewer (league settings + lock count).
 */
export const useWeekLockVisibility = (displayedWeek) => {
  const { user, getLocksForWeek, getMaxPicksForWeek } = useGameContext();
  const { settings } = useLeagueSettings();

  return useMemo(() => {
    const locksForWeek = getLocksForWeek(displayedWeek) || [];
    const maxPicksForWeek = getMaxPicksForWeek(displayedWeek) ?? 1;
    const userLocksCount = countUserLocksForWeek(locksForWeek, user?.id);
    const hasReachedMaxPicks = userLocksCount >= maxPicksForWeek;
    const showLockers = settings.show_lockers;
    const showLocks = settings.show_locks;

    return {
      userLocksCount,
      maxPicksForWeek,
      hasReachedMaxPicks,
      showLockersForViewer: canViewerSeeLockers(showLockers, userLocksCount, maxPicksForWeek),
      showLocksForViewer: canViewerSeeLocks(showLocks, userLocksCount, maxPicksForWeek),
      shouldShowUsername: (isUserLock) => shouldShowLockUsername({
        isUserLock,
        showLockers,
        userLocksCount,
        maxPicksForWeek,
      }),
      shouldShowPickInfo: () => shouldShowLockPickInfo({
        showLocks,
        userLocksCount,
        maxPicksForWeek,
      }),
      shouldShowLocksSection: (hasLocks) => shouldShowGameLocksSection({
        showLocks,
        userLocksCount,
        maxPicksForWeek,
        hasLocks,
      }),
    };
  }, [displayedWeek, user?.id, getLocksForWeek, getMaxPicksForWeek, settings.show_lockers, settings.show_locks]);
};
